import type { ReactNode } from 'react';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { OnboardingProgress, OnboardingMilestones } from '../types';
import { useProfile } from './ProfileContext';
import { useFeatures } from './FeaturesContext';
import { api } from '../api/client';

interface OnboardingContextType {
  onboarding: OnboardingProgress | null;
  loading: boolean;
  error: string | null;
  isFeatureEnabled: boolean;
  startTour: () => Promise<void>;
  skipTour: () => Promise<void>;
  dismissTourPermanently: () => Promise<void>;
  dismissChecklist: () => Promise<void>;
  dismissSecurityPrompt: () => Promise<void>;
  completeTour: () => Promise<void>;
  updateMilestone: (milestone: keyof OnboardingMilestones, value: boolean) => Promise<void>;
  refreshOnboarding: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const DEFAULT_ONBOARDING: OnboardingProgress = {
  tour_completed: false,
  tour_skipped: false,
  tour_skipped_permanently: false,
  checklist_dismissed: false,
  security_prompt_dismissed: false,
  milestones: {
    profile_complete: false,
    bio_complete: false,
    first_scan: false,
    first_survey: false,
    viewed_wellbeing: false,
    security_configured: false,
  },
  completed_at: null,
};

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const { profile, fetchProfile } = useProfile();
  const { features } = useFeatures();
  const [onboarding, setOnboarding] = useState<OnboardingProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Check if onboarding feature is enabled
  const isFeatureEnabled = features['ONB:tour'] ?? false;

  // Initialize onboarding state from profile ONCE (only on first load)
  useEffect(() => {
    // Only initialize once - don't overwrite state from subsequent profile fetches
    if (initialized) {
      return;
    }

    if (profile?.onboarding) {
      setOnboarding(profile.onboarding);
      setLoading(false);
      setInitialized(true);
    } else if (profile) {
      // Profile exists but no onboarding data - use defaults
      setOnboarding(DEFAULT_ONBOARDING);
      setLoading(false);
      setInitialized(true);
    }
  }, [profile, initialized]);

  /**
   * Refresh onboarding state from profile
   */
  const refreshOnboarding = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await fetchProfile();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh onboarding';
      setError(errorMessage);
      console.error('Failed to refresh onboarding:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchProfile]);

  /**
   * Update onboarding progress via API
   */
  const updateOnboarding = useCallback(async (data: Partial<OnboardingProgress>) => {
    try {
      setError(null);
      const updated = await api.patch<OnboardingProgress>('/users/me/onboarding', data);
      setOnboarding(updated);
      // Note: Don't call fetchProfile() here - it creates an infinite loop
      // The PATCH response already contains the updated onboarding state
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update onboarding';
      setError(errorMessage);
      console.error('Failed to update onboarding:', err);
      throw err;
    }
  }, []);

  /**
   * Start the onboarding tour
   * Resets tour skip flags
   */
  const startTour = useCallback(async () => {
    if (!isFeatureEnabled) {
      console.warn('Onboarding feature is disabled');
      return;
    }

    await updateOnboarding({
      tour_completed: false,
      tour_skipped: false,
    });
  }, [isFeatureEnabled, updateOnboarding]);

  /**
   * Skip the tour (can be reminded later)
   */
  const skipTour = useCallback(async () => {
    if (!isFeatureEnabled) {
      console.warn('Onboarding feature is disabled');
      return;
    }

    await updateOnboarding({
      tour_skipped: true,
    });
  }, [isFeatureEnabled, updateOnboarding]);

  /**
   * Permanently dismiss the tour reminder
   * Sets tour_skipped_permanently to true
   */
  const dismissTourPermanently = useCallback(async () => {
    if (!isFeatureEnabled) {
      console.warn('Onboarding feature is disabled');
      return;
    }

    await updateOnboarding({
      tour_skipped_permanently: true,
    });
  }, [isFeatureEnabled, updateOnboarding]);

  /**
   * Dismiss the onboarding checklist
   * Sets checklist_dismissed to true
   */
  const dismissChecklist = useCallback(async () => {
    if (!isFeatureEnabled) {
      console.warn('Onboarding feature is disabled');
      return;
    }

    await updateOnboarding({
      checklist_dismissed: true,
    });
  }, [isFeatureEnabled, updateOnboarding]);

  /**
   * Dismiss the security setup prompt
   * Sets security_prompt_dismissed to true
   */
  const dismissSecurityPrompt = useCallback(async () => {
    await updateOnboarding({
      security_prompt_dismissed: true,
    });
  }, [updateOnboarding]);

  /**
   * Complete the tour
   * Marks tour as completed
   */
  const completeTour = useCallback(async () => {
    if (!isFeatureEnabled) {
      console.warn('Onboarding feature is disabled');
      return;
    }

    await updateOnboarding({
      tour_completed: true,
      tour_skipped: false,
    });
  }, [isFeatureEnabled, updateOnboarding]);

  /**
   * Update a specific milestone
   */
  const updateMilestone = useCallback(async (
    milestone: keyof OnboardingMilestones,
    value: boolean
  ) => {
    if (!isFeatureEnabled) {
      console.warn('Onboarding feature is disabled');
      return;
    }

    const currentMilestones = onboarding?.milestones || DEFAULT_ONBOARDING.milestones;
    await updateOnboarding({
      milestones: {
        ...currentMilestones,
        [milestone]: value,
      },
    });
  }, [isFeatureEnabled, onboarding, updateOnboarding]);

  return (
    <OnboardingContext.Provider
      value={{
        onboarding,
        loading,
        error,
        isFeatureEnabled,
        startTour,
        skipTour,
        dismissTourPermanently,
        dismissChecklist,
        dismissSecurityPrompt,
        completeTour,
        updateMilestone,
        refreshOnboarding,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboardingContext must be used within an OnboardingProvider');
  }
  return context;
};
